import React, { useEffect, useState, useMemo } from "react";
import { AnimatedGift, AnimatedSnowflake, AnimatedStar } from "../icons/ChristmasIcons";

interface FloatingElementsProps {
  className?: string;
  variant?: "hero" | "subtle";
}

export const FloatingElements = ({ className = "", variant = "hero" }: FloatingElementsProps) => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Don't render on mobile for performance
  if (isMobile) return null;

  const elements = variant === "hero" ? [
    { Component: AnimatedGift, size: 60, left: "5%", top: "20%", delay: "0s", duration: "6s" },
    { Component: AnimatedGift, size: 45, left: "80%", top: "35%", delay: "2s", duration: "7s" },
    { Component: AnimatedStar, size: 40, left: "10%", top: "60%", delay: "1s", duration: "5s" },
    { Component: AnimatedStar, size: 35, left: "85%", top: "15%", delay: "3s", duration: "6s" },
    { Component: AnimatedSnowflake, size: 35, left: "15%", top: "80%", delay: "0.5s", duration: "8s" },
    { Component: AnimatedSnowflake, size: 30, left: "78%", top: "70%", delay: "2.5s", duration: "7s" },
    { Component: AnimatedSnowflake, size: 25, left: "50%", top: "5%", delay: "1.5s", duration: "9s" },
  ] : [
    { Component: AnimatedStar, size: 25, left: "5%", top: "30%", delay: "0s", duration: "8s" },
    { Component: AnimatedSnowflake, size: 20, left: "88%", top: "50%", delay: "2s", duration: "10s" },
    { Component: AnimatedStar, size: 20, left: "8%", top: "70%", delay: "4s", duration: "9s" },
  ];

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {elements.map((el, index) => (
        <div
          key={index}
          className="absolute"
          style={{
            left: el.left,
            top: el.top,
            animation: `floatElement ${el.duration} ease-in-out infinite`,
            animationDelay: el.delay,
            willChange: "transform",
          }}
        >
          <el.Component size={el.size} className="opacity-60" />
        </div>
      ))}
      
      <style>{`
        @keyframes floatElement {
          0%, 100% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
          25% {
            transform: translateY(-15px) translateX(8px) rotate(5deg);
          }
          50% {
            transform: translateY(-8px) translateX(-5px) rotate(-3deg);
          }
          75% {
            transform: translateY(-20px) translateX(5px) rotate(3deg);
          }
        }
      `}</style>
    </div>
  );
};

// Pre-calculated particles for consistent rendering
const PARTICLE_DATA = Array.from({ length: 30 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  color: i % 3 === 0 
    ? "hsl(42 75% 48%)" // gold
    : i % 3 === 1 
      ? "hsl(348 70% 50%)" // red
      : "hsl(152 55% 40%)", // green
  duration: 3 + Math.random() * 4,
  delay: Math.random() * 5,
  opacity: 0.4 + Math.random() * 0.4,
}));

// Particle system for magical dust effect
export const MagicParticles = ({ className = "" }: { className?: string }) => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Don't render on mobile for performance
  if (isMobile) return null;

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {PARTICLE_DATA.map((particle) => (
        <div
          key={particle.id}
          className="absolute w-1 h-1 rounded-full"
          style={{
            left: particle.left,
            top: particle.top,
            background: particle.color,
            animation: `particleFloat ${particle.duration}s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`,
            opacity: particle.opacity,
            willChange: "transform, opacity",
          }}
        />
      ))}
      
      <style>{`
        @keyframes particleFloat {
          0%, 100% {
            transform: translateY(0) translateX(0) scale(1);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-30px) translateX(10px) scale(1.5);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};
